import * as constants from "common/constants";

import axios from "axios";
import { useCallback } from "react";
import { useLocation } from "react-router-dom";
import { useLoginContext } from "common/context";
import useLogout from "./useLogout";
import { useNavigate } from "react-router";
import usePopAlert from "components/popAlert/hooks/usePopAlert";

export const METHOD = {
  GET: "get",
  POST: "post",
  PUT: "put",
  DELETE: "delete",
  PATCH: "patch",
};

const headers = {
  Accept: "application/json",
  Authorization: "",
};

function useAxios() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { showAlert } = usePopAlert();
  const { loginState, action } = useLoginContext();
  const { logout } = useLogout();

  const client = axios.create();

  client.interceptors.request.use((config) => {
    const accessToken = localStorage.getItem("accessToken");
    if (config.headers.Authorization == "Bearer ") {
      config.headers["Authorization"] = `Bearer ${accessToken}`;
    }
    return config;
  });

  client.interceptors.response.use(
    (response) => {
      if (response && response.data && response.data.refreshToken) {
        action.setRefreshToken(response.data.refreshToken);
        localStorage.setItem("refreshToken", response.data.refreshToken);
      }

      return response;
    },
    async (err) => {
      const originalRequest = err.config;

      // 401 또는 419 에러이고, 아직 토큰 갱신을 시도하지 않은 경우
      if (
        err.response &&
        err.response.status &&
        (err.response.status === 401 || err.response.status === 419) &&
        !originalRequest._retry
      ) {
        originalRequest._retry = true;

        const refreshToken = localStorage.getItem("refreshToken");
        if (refreshToken) {
          try {
            // refresh API 호출
            const refreshResponse = await axios.post(
              `${constants.API_BASE_URL}/auth/refresh`,
              { refreshToken },
              {
                headers: {
                  Authorization: `Bearer ${refreshToken}`,
                  Accept: "application/json",
                },
              }
            );

            if (
              refreshResponse.data &&
              refreshResponse.data.result_code === "0000"
            ) {
              const { accessToken, refreshToken: newRefreshToken } =
                refreshResponse.data.result_data;

              // 새로운 토큰들 저장
              action.setAccessToken(accessToken);
              localStorage.setItem("accessToken", accessToken);

              if (newRefreshToken) {
                action.setRefreshToken(newRefreshToken);
                localStorage.setItem("refreshToken", newRefreshToken);
              }

              // 원래 요청에 새로운 토큰으로 재시도
              originalRequest.headers.Authorization = `Bearer ${accessToken}`;
              return client(originalRequest);
            }
          } catch (refreshError) {
            // refresh API에서 420 에러 또는 기타 에러 발생
            if (refreshError.response && refreshError.response.status === 420) {
              // refreshToken도 만료됨
              logout();
              showAlert(
                "알림",
                "로그인 세션이 만료되었습니다.\n다시 로그인하세요",
                {
                  confirmHandler: () => {
                    sessionStorage.setItem("login_callback", pathname);
                    navigate("/login");
                  },
                  cancelHandler: () => {
                    sessionStorage.setItem("login_callback", pathname);
                    navigate("/login");
                  },
                }
              );
            } else {
              // 기타 refresh 에러
              logout();
              showAlert(
                "알림",
                "토큰 갱신에 실패했습니다.\n다시 로그인하세요",
                {
                  confirmHandler: () => {
                    sessionStorage.setItem("login_callback", pathname);
                    navigate("/login");
                  },
                  cancelHandler: () => {
                    sessionStorage.setItem("login_callback", pathname);
                    navigate("/login");
                  },
                }
              );
            }
            return Promise.reject(refreshError);
          }
        } else {
          // refreshToken이 없는 경우
          logout();
          showAlert(
            "알림",
            "더 이상 로그인 정보가 유효하지 않습니다.\n다시 로그인하세요",
            {
              confirmHandler: () => {
                sessionStorage.setItem("login_callback", pathname);
                navigate("/login");
              },
              cancelHandler: () => {
                sessionStorage.setItem("login_callback", pathname);
                navigate("/login");
              },
            }
          );
        }
      }

      return Promise.reject(err);
    }
  );

  const request = useCallback(
    (method, url, args, isBlob = false) => {
      let params = {};
      if (method === METHOD.GET) {
        params = { params: args };
      } else {
        params = { data: args };
      }
      if (!args) {
        params = {};
      }
      if (isBlob) {
        params = { ...params, responseType: "blob" };
      }

      const axiosConfig = {
        headers: {
          ...headers,
          Authorization: `Bearer ${loginState.accessToken}`,
        },
        method,
        url,
        timeout: 30000,
        ...params,
      };

      // console.info('axios config:', axiosConfig);
      return client(axiosConfig);
    },
    [loginState.accessToken]
  );

  const requestFunc = useCallback(
    async (method, url, args, isBlob = false) => {
      const response = await request(
        method,
        `${constants.API_BASE_URL}${url}`,
        args,
        isBlob
      );
      return response.data;
    },
    [request]
  );

  return { requestFunc };
}

export default useAxios;
